import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'lib/utils';
import { Loader2Icon } from 'lucide-react';
import { Slot } from 'radix-ui';
import * as React from 'react';

/**
 * Figma: "Button - Nova" (Button & Icon Button page).
 *
 * Variants Primary / Secondary / Outline / Ghost / Link / Destructive, sizes
 * Default / Large (+ Small and icon sizes from shadcn). Buttons are capsules
 * (`rounded-full`, Figma `radius-full`) and the default size is 32px tall with
 * a 14px Medium label, 10px inline padding and a 6px gap, as in the library.
 *
 * Focus is one paon ring for every variant, 2px wide and 2px off the edge,
 * distinct from the invalid state. The two destructive variants follow the
 * "Boutons alertes" page: `destructive` (the alerting one, white on red, for
 * important and irreversible actions, with the trash icon and an explicit
 * label such as "Supprimer le message") and `destructive-subtle` (the light
 * one, for less critical destructive actions, whose 1px border never goes).
 * Their hover and pressed colours are tokens, not an opacity change.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background aria-invalid:ring-ring-destructive/20 aria-invalid:border-ring-destructive cursor-pointer",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive-hover active:bg-destructive-active',
        'destructive-subtle':
          'border border-destructive bg-destructive-subtle text-destructive-subtle-foreground hover:border-destructive-hover hover:bg-destructive-subtle-hover hover:text-destructive-active active:border-destructive-active active:bg-destructive-subtle-active active:text-destructive-active',
        outline: 'border border-border bg-card text-foreground shadow-xs hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        /** The secondary pill on the brand's deep teal (the messaging stories), its text in the general foreground. */
        'secondary-inverse': 'bg-secondary text-foreground hover:bg-secondary/80',
        /** On the brand's deep teal, for a compact action inside a white card. */
        inverse: 'bg-inverse text-inverse-foreground hover:bg-inverse/90',
        ghost: 'text-foreground hover:bg-accent hover:text-accent-foreground',
        /** A ghost button on the brand's deep teal (`bg-inverse`). */
        'ghost-inverse': 'text-inverse-foreground hover:bg-inverse-foreground/10',
        link: 'text-link underline-offset-4 hover:underline hover:text-link-hover',
        /** A link whose action has a consequence worth a second look (no Figma counterpart yet). */
        'link-warning': 'text-warning underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-8 px-2.5 py-1.5 has-[>svg]:px-2',
        sm: 'h-7 gap-1 px-2 text-xs has-[>svg]:px-1.5',
        lg: 'h-10 px-4 text-base has-[>svg]:px-3',
        /** Full-width mobile call to action. */
        xl: 'h-12 px-6 text-base has-[>svg]:px-4',
        icon: 'size-8',
        'icon-sm': 'size-7',
        'icon-lg': 'size-10',
        /** Beside a field, matching its height (the message composer's send button). */
        'icon-xl': 'size-11',
        /** A dialog's or a full-screen view's close control: a generous touch target. */
        'icon-2xl': "size-12 [&_svg:not([class*='size-'])]:size-6",
      },
    },
    compoundVariants: [
      // A link is inline text: no capsule box, so it aligns with the copy around it
      { variant: 'link', class: 'h-auto px-0 py-0' },
      { variant: 'link-warning', class: 'h-auto px-0 py-0' },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /** Shows a spinner in place of the leading icon and disables the button. */
    loading?: boolean;
    /**
     * Marks a toggle button as chosen. Renders `aria-pressed` and, for the
     * functional test suite, `data-selected`.
     */
    selected?: boolean;
  };

function Button({
  className,
  variant,
  size,
  asChild = false,
  loading,
  selected,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="button"
      data-selected={selected === undefined ? undefined : selected}
      aria-pressed={selected}
      className={cn(buttonVariants({ variant, size, className }), selected && 'ring-2 ring-inset ring-primary')}
      disabled={disabled || loading}
      {...props}
    >
      {asChild ? (
        // Slot needs exactly one child; the spinner is only for real buttons
        children
      ) : (
        <>
          {loading && <Loader2Icon className="animate-spin" />}
          {children}
        </>
      )}
    </Comp>
  );
}

export { Button, buttonVariants };
